import { type RefObject, useCallback, useMemo, useRef } from 'react';

import type { AutoSaveCoordinationContextValue } from './useAutoSaveCoordination';

export interface AutoSaveFreezeOwner {
  /**
   * Mint a new freeze-owner token via `coordination.freezeAutoSave()`, store
   * it as the most recently minted token (readable via `currentToken`), and
   * return it. Each call arms an independent hold on the shared,
   * reference-counted provider freeze — see `freezeAutoSave` on
   * `AutoSaveCoordinationContextValue` for the reference-counting contract
   * (order-independent release, multiple concurrent owners).
   */
  freeze: () => number;
  /**
   * Record a token the provider minted on this owner's behalf — i.e. the
   * `freezeToken` returned by `coordination.settleAndFreeze()`, which takes the
   * freeze itself so nothing can arm between "saves are idle" and "frozen".
   * Makes it the `currentToken` exactly as `freeze()` would, and returns it.
   */
  adopt: (token: number) => number;
  /**
   * Release a specific token via `coordination.unfreezeAutoSave(token)`. A
   * thin, unconditional pass-through: this hook does not decide when or how
   * many times to release — each call site keeps its own logic for that
   * (a footer releases once, on failure or navigation-away; a card exit
   * keeps its own armed-flag guard around the call). Passing a stale token
   * — one a newer freeze has since superseded — is safe and simply no-ops,
   * per `unfreezeAutoSave`'s own contract.
   */
  release: (token: number) => void;
  /**
   * Always reflects the token from the most recent `freeze()` call. Lets a
   * caller that didn't itself call `freeze()` during this render — e.g. a
   * navigation-cleanup effect that only ever releases — still read the
   * current owner token.
   */
  currentToken: RefObject<number>;
}

/**
 * Extracts the "mint a freeze-owner token, hold it in a ref, release only
 * that token later" idiom shared by every point-of-no-return decision that
 * freezes autosave via `AutoSaveCoordinationContextValue.freezeAutoSave()` /
 * `unfreezeAutoSave(token)` — the approval footers' approve/reject paths and
 * a proposal card's own "I don't have this information" exit.
 *
 * This hook owns ONLY what is genuinely duplicated across those call sites:
 * minting a token, remembering the most recently minted one (replacing each
 * site's own `useRef(0)` token ref), and bridging to the coordination API.
 * It deliberately does NOT unify the different release shapes those call
 * sites use — a footer's single release on failure vs. a card exit's
 * multi-point conditional release guarded by its own armed flag — since
 * flattening that into one model would risk reintroducing the exact race
 * the freeze exists to prevent.
 */
export function useAutoSaveFreezeOwner(
  coordination: AutoSaveCoordinationContextValue | null
): AutoSaveFreezeOwner {
  const currentToken = useRef(0);

  const freeze = useCallback((): number => {
    const token = coordination?.freezeAutoSave() ?? 0;
    currentToken.current = token;
    return token;
  }, [coordination]);

  const adopt = useCallback((token: number): number => {
    currentToken.current = token;
    return token;
  }, []);

  const release = useCallback(
    (token: number): void => {
      coordination?.unfreezeAutoSave(token);
    },
    [coordination]
  );

  return useMemo(
    () => ({ freeze, adopt, release, currentToken }),
    [freeze, adopt, release]
  );
}
